import { Fragment, useMemo, useState, type JSX } from 'react'
import { Download, ExternalLink, ArrowUpDown, ChevronRight, ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router'

export interface ModelUsageRow {
  key: string
  provider: string
  taskType: string
  requests: number
  inputTokens: number
  outputTokens: number
  costUsd: number
  sharePct: number
  cap?: {
    capUsd: number
    usedPct: number
    action: string
    targetModel?: string | null
  }
  isFree?: boolean
  pricingSource?: 'built_in' | 'custom' | 'self_hosted'
  providerStatus?: 'connected' | 'no_key' | 'unknown'
}

export type UsageTableTab = 'model' | 'provider' | 'agent' | 'surface'

interface ModelUsageTableProps {
  rows: ModelUsageRow[]
  totals: {
    requests: number
    inputTokens: number
    outputTokens: number
    costUsd: number
  }
  loading?: boolean | undefined
  activeTab: UsageTableTab
  onTabChange: (tab: UsageTableTab) => void
  onEditCap: (
    model: string,
    currentCap?: number,
    currentAction?: 'block' | 'degrade',
    currentTargetModel?: string | null,
  ) => void
  period: string
  providers?: string[] | undefined
  selectedProvider?: string | undefined
  onSelectProvider?: ((provider: string) => void) | undefined
}

function formatTokens(n: number): string {
  if (n <= 0) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return n.toLocaleString()
}

function formatTaskType(t: string): string {
  if (!t) return 'General'
  const lower = t.toLowerCase()
  if (lower === 'chat') return 'Chat'
  if (lower === 'embedding' || lower === 'embeddings') return 'Embedding'
  if (lower === 'code') return 'Code'
  if (lower === 'guardrail') return 'Guardrail'
  if (lower === 'unattributed') return 'Unattributed'
  return t.charAt(0).toUpperCase() + t.slice(1)
}

export default function ModelUsageTable({
  rows,
  totals,
  loading = false,
  activeTab,
  onTabChange,
  onEditCap,
  period,
  providers,
  selectedProvider,
  onSelectProvider,
}: ModelUsageTableProps): JSX.Element {
  const navigate = useNavigate()
  const [sortField, setSortField] = useState<'key' | 'requests' | 'costUsd'>('costUsd')
  const [sortAsc, setSortAsc] = useState(false)
  const [showAllModels, setShowAllModels] = useState(false)

  function toggleSort(field: 'key' | 'requests' | 'costUsd'): void {
    if (sortField === field) {
      setSortAsc(!sortAsc)
    } else {
      setSortField(field)
      setSortAsc(false)
    }
  }

  // Export CSV helper
  function handleExportCsv(): void {
    const headers = [
      'Name',
      'Provider',
      'Task / Type',
      'Requests',
      'Input Tokens',
      'Output Tokens',
      'Cost USD',
      'Share Pct',
    ]
    const csvRows = [headers.join(',')]

    for (const r of rows) {
      csvRows.push(
        [
          `"${r.key.replace(/"/g, '""')}"`,
          `"${r.provider}"`,
          `"${r.taskType}"`,
          r.requests,
          r.inputTokens,
          r.outputTokens,
          r.costUsd.toFixed(4),
          `${r.sharePct}%`,
        ].join(','),
      )
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai-usage-${activeTab}-${period}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Active vs inactive model breakdown
  const { activeRows, inactiveRows, needsKeyCount, unusedCount } = useMemo(() => {
    if (activeTab !== 'model') {
      return { activeRows: rows, inactiveRows: [], needsKeyCount: 0, unusedCount: 0 }
    }
    const active: ModelUsageRow[] = []
    const inactive: ModelUsageRow[] = []
    let needsKey = 0

    for (const r of rows) {
      const isUnattributed = r.taskType === 'unattributed' || r.key === '(model not recorded)'
      const hasActivity = r.requests > 0 || r.cap !== undefined
      if (isUnattributed || hasActivity) {
        active.push(r)
      } else {
        inactive.push(r)
        if (r.providerStatus === 'no_key') {
          needsKey++
        }
      }
    }
    return {
      activeRows: active,
      inactiveRows: inactive,
      needsKeyCount: needsKey,
      unusedCount: inactive.length - needsKey,
    }
  }, [rows, activeTab])

  // Grouped and sorted items to display
  const groupedData = useMemo(() => {
    const rowsToDisplay =
      activeTab === 'model' && !showAllModels ? activeRows : rows

    const sortFn = (a: ModelUsageRow, b: ModelUsageRow) => {
      const factor = sortAsc ? 1 : -1
      if (sortField === 'requests') return (a.requests - b.requests) * factor
      if (sortField === 'costUsd') return (a.costUsd - b.costUsd) * factor
      return a.key.localeCompare(b.key) * factor
    }

    if (activeTab !== 'model') {
      const sorted = [...rowsToDisplay].sort(sortFn)
      return [{ groupName: '', items: sorted }]
    }

    const chats: ModelUsageRow[] = []
    const embeddings: ModelUsageRow[] = []
    const otherTasks: ModelUsageRow[] = []
    const unattributed: ModelUsageRow[] = []

    for (const r of rowsToDisplay) {
      if (r.taskType === 'unattributed' || r.key === '(model not recorded)') {
        unattributed.push(r)
      } else if (r.taskType === 'chat') {
        chats.push(r)
      } else if (r.taskType === 'embedding' || r.taskType === 'embeddings') {
        embeddings.push(r)
      } else {
        otherTasks.push(r)
      }
    }

    const result = []
    if (chats.length > 0) result.push({ groupName: 'CHAT MODELS', items: chats.sort(sortFn) })
    if (embeddings.length > 0) result.push({ groupName: 'EMBEDDINGS', items: embeddings.sort(sortFn) })
    if (otherTasks.length > 0) result.push({ groupName: 'OTHER TASK MODELS', items: otherTasks.sort(sortFn) })
    if (unattributed.length > 0) result.push({ groupName: 'UNATTRIBUTED', items: unattributed })

    return result
  }, [rows, activeRows, showAllModels, activeTab, sortField, sortAsc])

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-2xs">
      {/* Header with Title and Tabs */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Model usage & limits</h4>
          <p className="text-xs text-gray-400">
            {activeTab === 'model'
              ? 'Only models configured in AI Configuration are listed.'
              : activeTab === 'provider'
                ? 'Usage and spend aggregated across configured AI providers.'
                : activeTab === 'agent'
                  ? 'Usage and spend aggregated across AI agents.'
                  : 'Usage and spend broken down by invocation surface.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {activeTab === 'model' && providers && providers.length > 0 && (
            <div className="mr-2 flex items-center gap-1.5 text-xs text-gray-500">
              <span className="font-medium text-gray-500">Provider:</span>
              <select
                aria-label="Filter by provider"
                value={selectedProvider ?? ''}
                onChange={(e) => onSelectProvider?.(e.target.value)}
                className="rounded-xs border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 shadow-2xs focus:border-[#3454cf] focus:outline-hidden"
              >
                <option value="">All providers</option>
                {providers.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tab Controls (accessible role=tablist) */}
          <div
            role="tablist"
            aria-label="Usage grouping dimensions"
            className="inline-flex rounded-sm bg-gray-100 p-0.5 text-xs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'model'}
              onClick={() => onTabChange('model')}
              className={`rounded-xs px-3 py-1 font-semibold transition-colors ${
                activeTab === 'model'
                  ? 'bg-[#3454cf] text-white shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Model
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'provider'}
              onClick={() => onTabChange('provider')}
              className={`rounded-xs px-3 py-1 font-semibold transition-colors ${
                activeTab === 'provider'
                  ? 'bg-[#3454cf] text-white shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Provider
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'agent'}
              onClick={() => onTabChange('agent')}
              className={`rounded-xs px-3 py-1 font-semibold transition-colors ${
                activeTab === 'agent'
                  ? 'bg-[#3454cf] text-white shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Agent
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'surface'}
              onClick={() => onTabChange('surface')}
              className={`rounded-xs px-3 py-1 font-semibold transition-colors ${
                activeTab === 'surface'
                  ? 'bg-[#3454cf] text-white shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Surface
            </button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] font-semibold text-gray-400">
              {/* Column 1: Model / Entity */}
              <th
                className="cursor-pointer pb-2.5 font-medium hover:text-gray-700"
                onClick={() => toggleSort('key')}
              >
                <span className="inline-flex items-center gap-1">
                  {activeTab === 'model'
                    ? 'Model'
                    : activeTab === 'provider'
                      ? 'Provider'
                      : activeTab === 'agent'
                        ? 'Agent'
                        : 'Surface'}
                  <ArrowUpDown className="h-3 w-3" />
                </span>
              </th>

              {/* Column 2: Requests */}
              <th
                className="cursor-pointer pb-2.5 font-medium hover:text-gray-700"
                onClick={() => toggleSort('requests')}
              >
                <span className="inline-flex items-center gap-1">
                  Requests
                  <ArrowUpDown className="h-3 w-3" />
                </span>
              </th>

              {/* Column 3: Tokens (compact) */}
              <th className="pb-2.5 font-medium">Tokens</th>

              {/* Column 4: Cost */}
              <th
                className="cursor-pointer pb-2.5 font-medium hover:text-gray-700"
                onClick={() => toggleSort('costUsd')}
              >
                <span className="inline-flex items-center gap-1">
                  Cost
                  <ArrowUpDown className="h-3 w-3" />
                </span>
              </th>

              {/* Columns for By Model */}
              {activeTab === 'model' && <th className="pb-2.5 font-medium">Cap</th>}
              {activeTab === 'model' && <th className="pb-2.5 font-medium">Cap used</th>}

              {/* Action column */}
              <th className="pb-2.5 text-right font-medium">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-xs text-gray-400">
                  Loading usage and limit data…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-xs text-gray-400">
                  No usage recorded for this period.
                </td>
              </tr>
            ) : (
              groupedData.map((group) => (
                <Fragment key={group.groupName || 'all'}>
                  {group.groupName && (
                    <tr className="bg-gray-50/70">
                      <td
                        colSpan={7}
                        className="py-1.5 px-2 text-[10px] font-bold tracking-wider text-gray-400 uppercase"
                      >
                        {group.groupName}
                      </td>
                    </tr>
                  )}

                  {group.items.map((row) => {
                    const isNoKey = row.providerStatus === 'no_key'
                    const isUnattributed = row.taskType === 'unattributed'
                    const isFree = row.isFree || row.pricingSource === 'self_hosted'
                    const totalTokens = row.inputTokens + row.outputTokens
                    const tokenTooltip = `${formatTokens(row.inputTokens)} in / ${formatTokens(
                      row.outputTokens,
                    )} out`

                    const dotColor = isNoKey
                      ? 'bg-amber-500'
                      : row.providerStatus === 'connected'
                        ? 'bg-emerald-500'
                        : 'bg-gray-400'

                    return (
                      <tr
                        key={`${row.provider}:${row.key}`}
                        className={`transition-colors hover:bg-gray-50/50 ${
                          isNoKey ? 'opacity-70' : ''
                        }`}
                      >
                        {/* 1. Model Name with status dot + Provider · Type underneath */}
                        <td className="py-2.5">
                          <div className="font-mono text-xs font-semibold text-gray-900">
                            {row.key}
                          </div>
                          {activeTab === 'model' && (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                              <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
                              <span>
                                {row.provider} · {formatTaskType(row.taskType)}
                              </span>
                            </div>
                          )}
                          {activeTab === 'provider' && (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                              <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
                              <span>
                                {isNoKey
                                  ? 'No API key'
                                  : isFree
                                    ? 'Self-hosted'
                                    : 'Connected'}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* 2. Requests */}
                        <td className="py-2.5 whitespace-nowrap text-gray-700">
                          {isNoKey ? '—' : row.requests.toLocaleString()}
                        </td>

                        {/* 3. Tokens (Compact formatted with tooltip) */}
                        <td className="py-2.5 whitespace-nowrap text-gray-700" title={tokenTooltip}>
                          {isNoKey ? '—' : formatTokens(totalTokens)}
                        </td>

                        {/* 4. Cost */}
                        <td className="py-2.5 font-mono whitespace-nowrap font-medium text-gray-900">
                          {isNoKey ? '—' : `$${row.costUsd.toFixed(2)}`}
                        </td>

                        {/* 5. Monthly cap (if By Model tab) */}
                        {activeTab === 'model' && (
                          <td className="py-2.5 whitespace-nowrap">
                            {isNoKey ? (
                              '—'
                            ) : isFree ? (
                              <span className="text-gray-500">Free</span>
                            ) : row.cap ? (
                              <span className="font-mono">${row.cap.capUsd.toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        )}

                        {/* 6. Cap used bar (if By Model tab) */}
                        {activeTab === 'model' && (
                          <td className="py-2.5 whitespace-nowrap">
                            {isNoKey ? (
                              '—'
                            ) : isFree ? (
                              <span className="text-[11px] text-gray-400">— Self-hosted</span>
                            ) : row.cap ? (
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                                  <div
                                    className={`h-full ${
                                      row.cap.usedPct >= 100
                                        ? 'bg-red-500'
                                        : row.cap.usedPct >= 80
                                          ? 'bg-amber-500'
                                          : 'bg-emerald-500'
                                    }`}
                                    style={{ width: `${Math.min(row.cap.usedPct, 100)}%` }}
                                  />
                                </div>
                                <span className="text-[11px] text-gray-600">
                                  {row.cap.usedPct.toFixed(0)}%
                                </span>
                              </div>
                            ) : (
                              <span className="text-[11px] text-gray-400">—</span>
                            )}
                          </td>
                        )}

                        {/* 7. Action */}
                        <td className="py-2.5 text-right whitespace-nowrap">
                          {isNoKey ? (
                            <button
                              type="button"
                              onClick={() => navigate('/admin/ai-config')}
                              className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#3454cf] hover:underline"
                            >
                              Add key <ExternalLink className="h-2.5 w-2.5" />
                            </button>
                          ) : isUnattributed ? (
                            <button
                              type="button"
                              onClick={() => navigate('/admin/ai-audit?status=error')}
                              className="text-xs font-semibold text-[#3454cf] hover:underline"
                            >
                              Fix →
                            </button>
                          ) : activeTab === 'provider' ? (
                            <button
                              type="button"
                              onClick={() => navigate('/admin/ai-config')}
                              className="text-xs font-semibold text-[#3454cf] hover:underline"
                            >
                              Configure →
                            </button>
                          ) : isFree ? (
                            <span className="text-[11px] text-gray-300">—</span>
                          ) : activeTab === 'model' ? (
                            <button
                              type="button"
                              onClick={() =>
                                onEditCap(
                                  row.key,
                                  row.cap?.capUsd,
                                  (row.cap?.action as 'block' | 'degrade') ?? 'block',
                                  row.cap?.targetModel,
                                )
                              }
                              className="text-xs font-semibold text-[#3454cf] hover:underline"
                            >
                              {row.cap ? 'Edit' : 'Set cap'}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </Fragment>
              ))
            )}
          </tbody>

          {/* Total row and Export CSV */}
          <tfoot>
            <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
              <td className="py-3 font-semibold">Total</td>
              <td className="py-3">{totals.requests.toLocaleString()}</td>
              <td
                className="py-3"
                title={`${formatTokens(totals.inputTokens)} in / ${formatTokens(
                  totals.outputTokens,
                )} out`}
              >
                {formatTokens(totals.inputTokens + totals.outputTokens)}
              </td>
              <td className="py-3 font-mono">${totals.costUsd.toFixed(2)}</td>
              {activeTab === 'model' && <td className="py-3" />}
              {activeTab === 'model' && <td className="py-3" />}
              <td className="py-3 text-right">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-2xs transition-colors hover:bg-gray-50"
                >
                  <Download className="h-3 w-3" /> Export CSV
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Expandable Footer for Inactive Models (only in By Model tab) */}
      {activeTab === 'model' && inactiveRows.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <button
            type="button"
            onClick={() => setShowAllModels(!showAllModels)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3454cf] hover:underline"
          >
            {showAllModels ? (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Hide {inactiveRows.length} inactive models
              </>
            ) : (
              <>
                <ChevronRight className="h-3.5 w-3.5" />
                Show {inactiveRows.length} more configured models ({needsKeyCount} need an API key ·{' '}
                {unusedCount} unused)
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
